import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Sparkles, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';

const API = '/api';

const PreCallSurvey = () => {
  const [searchParams] = useSearchParams();
  const appointmentId = searchParams.get('appointment_id');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    city: '',
    province_state: '',
    country: '',
    desire_for_change: 5,
    when_realized_change: '',
    changes_looking_for: [],
    money_needed_monthly: '',
    money_needed_yearly: '',
    why_online_business: '',
    marketing_budget: '',
    marketing_budget_other: '',
    readiness_level: '',
    acknowledged: false
  });

  const changesOptions = [
    'Increase income',
    'More time with family/friends',
    'More time to travel',
    'Own my own business',
    'More money to help family'
  ];

  const budgetOptions = [
    '$0 - $100 (Starting with organic strategies)',
    '$100 - $500',
    '$500 and above'
  ];

  const readinessOptions = [
    'Ready and excited to work with you',
    'Just curious',
    'Seeing my options'
  ];

  useEffect(() => {
    // Check if survey already submitted
    const checkSurveyStatus = async () => {
      if (!appointmentId) return;
      try {
        const response = await axios.get(`${API}/survey/status/${appointmentId}`);
        if (response.data.submitted) {
          setAlreadySubmitted(true);
        }
      } catch (error) {
        // Survey not found, that's OK
      }
    };
    checkSurveyStatus();
  }, [appointmentId]);

  const handleCheckboxChange = (option) => {
    setFormData(prev => ({
      ...prev,
      changes_looking_for: prev.changes_looking_for.includes(option)
        ? prev.changes_looking_for.filter(item => item !== option)
        : [...prev.changes_looking_for, option]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.acknowledged) {
      toast.error('Please acknowledge that you have opted for your coaching call');
      return;
    }

    if (!appointmentId) {
      toast.error('Invalid survey link. Please use the link from your email.');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/survey/submit`, {
        appointment_id: appointmentId,
        ...formData
      });
      setSubmitted(true);
      toast.success('Survey submitted successfully! See you on the call.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit survey');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (alreadySubmitted) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl p-8 shadow-lg max-w-md text-center"
        >
          <CheckCircle className="w-16 h-16 text-[#25D366] mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Survey Already Submitted</h2>
          <p className="text-[#525252]">
            Thank you! You've already completed this survey. We look forward to your coaching call!
          </p>
        </motion.div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl p-8 shadow-lg max-w-md text-center"
        >
          <CheckCircle className="w-16 h-16 text-[#25D366] mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Thank You!</h2>
          <p className="text-[#525252]">
            Your survey has been submitted. We're excited to speak with you on your coaching call!
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="w-8 h-8 text-[#FF4500]" />
            <h1 className="text-2xl font-black text-[#0A0A0A]">MNA Consult</h1>
          </div>
          <h2 className="text-3xl font-bold text-[#0A0A0A] mb-2">Pre-Call Survey</h2>
          <p className="text-[#525252]">Please complete this survey before your coaching call</p>
        </motion.div>

        {/* Survey Form */}
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl shadow-lg p-8 space-y-6"
        >
          {/* Location Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-[#0A0A0A] border-b pb-2">Your Location</h3>
            
            <div>
              <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
                City you live in: <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                className="w-full h-12 px-4 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                placeholder="Enter your city"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
                Province/State you live in: <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.province_state}
                onChange={(e) => setFormData({...formData, province_state: e.target.value})}
                className="w-full h-12 px-4 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                placeholder="Enter your province/state"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
                Country You Live In: <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.country}
                onChange={(e) => setFormData({...formData, country: e.target.value})}
                className="w-full h-12 px-4 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                placeholder="Enter your country"
              />
            </div>
          </div>

          {/* Desire for Change */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-[#0A0A0A] border-b pb-2">Your Motivation</h3>
            
            <div>
              <label className="block text-sm font-medium text-[#0A0A0A] mb-4">
                How Strong Is Your Desire For Change? <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-4">
                <span className="text-sm text-[#525252]">Strong</span>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={formData.desire_for_change}
                  onChange={(e) => setFormData({...formData, desire_for_change: parseInt(e.target.value)})}
                  className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#FF4500]"
                />
                <span className="text-sm text-[#525252]">Very Strong</span>
              </div>
              <p className="text-center text-2xl font-bold text-[#FF4500] mt-2">{formData.desire_for_change}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
                When did you realize that your life needed to change? <span className="text-red-500">*</span>
              </label>
              <textarea
                required
                value={formData.when_realized_change}
                onChange={(e) => setFormData({...formData, when_realized_change: e.target.value})}
                className="w-full h-24 px-4 py-3 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500] resize-none"
                placeholder="Share your story..."
              />
            </div>
          </div>

          {/* Changes Looking For */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-[#0A0A0A] border-b pb-2">What changes are you looking for?</h3>
            <p className="text-sm text-[#525252]">Select all that apply:</p>
            
            <div className="space-y-3">
              {changesOptions.map((option) => (
                <label key={option} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.changes_looking_for.includes(option)}
                    onChange={() => handleCheckboxChange(option)}
                    className="w-5 h-5 rounded border-gray-300 text-[#FF4500] focus:ring-[#FF4500]"
                  />
                  <span className="text-[#0A0A0A]">{option}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Money Needed */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-[#0A0A0A]">
              Considering the boxes you checked off above, how much money would you need to make all of those things possible?
            </label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-[#525252] mb-1">Per Month</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#525252]">$</span>
                  <input
                    type="text"
                    value={formData.money_needed_monthly}
                    onChange={(e) => setFormData({...formData, money_needed_monthly: e.target.value})}
                    className="w-full h-12 pl-8 pr-4 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                    placeholder="0"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-[#525252] mb-1">Per Year</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#525252]">$</span>
                  <input
                    type="text"
                    value={formData.money_needed_yearly}
                    onChange={(e) => setFormData({...formData, money_needed_yearly: e.target.value})}
                    className="w-full h-12 pl-8 pr-4 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Why Online Business */}
          <div>
            <label className="block text-sm font-medium text-[#0A0A0A] mb-2">
              Why choose to start an online business right now? <span className="text-red-500">*</span>
            </label>
            <textarea
              required
              value={formData.why_online_business}
              onChange={(e) => setFormData({...formData, why_online_business: e.target.value})}
              className="w-full h-24 px-4 py-3 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500] resize-none"
              placeholder="Tell us your reasons..."
            />
          </div>

          {/* Marketing Budget */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-[#0A0A0A]">
              What is your monthly marketing budget? <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-[#525252]">
              (If you're starting with little to no budget, don't worry - we'll also teach you free, organic marketing strategies to help you grow.)
            </p>
            
            <div className="space-y-3">
              {budgetOptions.map((option) => (
                <label key={option} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="marketing_budget"
                    value={option}
                    checked={formData.marketing_budget === option}
                    onChange={(e) => setFormData({...formData, marketing_budget: e.target.value})}
                    className="w-5 h-5 border-gray-300 text-[#FF4500] focus:ring-[#FF4500]"
                  />
                  <span className="text-[#0A0A0A]">{option}</span>
                </label>
              ))}
              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  name="marketing_budget"
                  value="other"
                  checked={formData.marketing_budget === 'other'}
                  onChange={(e) => setFormData({...formData, marketing_budget: e.target.value})}
                  className="w-5 h-5 border-gray-300 text-[#FF4500] focus:ring-[#FF4500]"
                />
                <span className="text-[#0A0A0A]">Other:</span>
                <input
                  type="text"
                  value={formData.marketing_budget_other}
                  onChange={(e) => setFormData({...formData, marketing_budget_other: e.target.value, marketing_budget: 'other'})}
                  className="flex-1 h-10 px-4 bg-[#F4F4F5] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4500]"
                  placeholder="Specify amount"
                />
              </div>
            </div>
          </div>

          {/* Readiness Level */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-[#0A0A0A]">
              Where are you at right now in terms of moving forward? <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-[#525252]">(No pressure - this helps us tailor the coaching experience to your needs)</p>
            
            <div className="space-y-3">
              {readinessOptions.map((option) => (
                <label key={option} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="readiness_level"
                    value={option}
                    checked={formData.readiness_level === option}
                    onChange={(e) => setFormData({...formData, readiness_level: e.target.value})}
                    className="w-5 h-5 border-gray-300 text-[#FF4500] focus:ring-[#FF4500]"
                  />
                  <span className="text-[#0A0A0A]">{option}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Acknowledgment */}
          <div className="bg-[#F4F4F5] rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.acknowledged}
                onChange={(e) => setFormData({...formData, acknowledged: e.target.checked})}
                className="w-5 h-5 mt-0.5 rounded border-gray-300 text-[#FF4500] focus:ring-[#FF4500]"
              />
              <span className="text-sm text-[#0A0A0A]">
                By checking this box you acknowledge that you have opted for your coaching call today
              </span>
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-14 bg-[#FF4500] text-white rounded-lg hover:bg-[#E03E00] transition-all duration-300 font-semibold text-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_8px_16px_rgba(255,69,0,0.3)]"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Submit Survey
              </>
            )}
          </button>
        </motion.form>
      </div>
    </div>
  );
};

export default PreCallSurvey;
